// Small pure geometry helpers used by sim and render.
import type { MapData, Point } from './types';

export function dist(ax: number, ay: number, bx: number, by: number): number {
  const dx = ax - bx;
  const dy = ay - by;
  return Math.sqrt(dx * dx + dy * dy);
}

/** Ray-casting point-in-polygon. Points on an edge count as inside. */
export function pointInPolygon(x: number, y: number, polygon: Point[]): boolean {
  let inside = false;
  const n = polygon.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const pi = polygon[i];
    const pj = polygon[j];
    if (!pi || !pj) continue;
    const [xi, yi] = pi;
    const [xj, yj] = pj;
    const crosses = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (crosses) inside = !inside;
  }
  return inside;
}

/** Shortest distance from a point to a polyline. */
export function distanceToPath(x: number, y: number, points: Point[]): number {
  let best = Infinity;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    if (!a || !b) continue;
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const lenSq = dx * dx + dy * dy;
    const t =
      lenSq === 0 ? 0 : Math.max(0, Math.min(1, ((x - a[0]) * dx + (y - a[1]) * dy) / lenSq));
    const px = a[0] + t * dx - x;
    const py = a[1] + t * dy - y;
    best = Math.min(best, Math.sqrt(px * px + py * py));
  }
  return best;
}

/** Half-width of the strip along a river centreline where nothing may be built. */
export const RIVER_CLEARANCE = 9;

/**
 * Where the player may build: on land, out of the lakes, off the river. There is no
 * border; the board rectangle is the only limit. Sea is everything that is not land.
 */
export function isBuildable(x: number, y: number, map: MapData): boolean {
  if (x < 0 || y < 0 || x > map.size.w || y > map.size.h) return false;
  let onLand = false;
  for (const ring of map.land) {
    if (pointInPolygon(x, y, ring)) {
      onLand = true;
      break;
    }
  }
  if (!onLand) return false;
  for (const lake of map.lakes) {
    if (pointInPolygon(x, y, lake.polygon)) return false;
  }
  for (const river of map.rivers) {
    if (distanceToPath(x, y, river.points) < RIVER_CLEARANCE) return false;
  }
  return true;
}
